import * as c from '@iamo/custody-lib';
import { IConnection } from './Connection';
import { Messaging } from '@iamo/notification';
import { TaskManager, Task, TaskState, IDataStorage } from '@iamo/utils';

export enum EthEventTypes {
    ExecutionFailure = 'ExecutionFailure',
    ExecutionSuccess = 'ExecutionSuccess',
    ExecutionFromModuleSuccess = 'ExecutionFromModuleSuccess',
    ExecutionFromModuleFailure = 'ExecutionFromModuleFailure',
    ApproveHash = 'ApproveHash',
    ChangedMasterCopy = 'ChangedMasterCopy',
    ChangedThreshold = 'ChangedThreshold',
    AddedOwner = 'AddedOwner',
    RemovedOwner = 'RemovedOwner',
    EnabledModule = 'EnabledModule',
    DisabledModule = 'DisabledModule',
    EthTransferred = 'EthTransferred',

    /** --- Module events --- */
    TransactionUnderLimitExecuted = 'TransactionUnderLimitExecuted',
    ChallengeInitiated = 'ChallengeInitiated',
    ChallengeJoined = 'ChallengeJoined',
    HeartbeatSuccessful = 'HeartbeatSuccessful',
    HeartbeatFailed = 'HeartbeatFailed',
}


/**
 * The TaskManagerWallet-class of the wallet
 */
export class TaskManagerWallet {

    // The blockchain property of the backend connection object.
    connection: IConnection

    /**
     * initializes the class
     * @param connection The backend connection object.
     */
    constructor(connection: IConnection){
        this.connection = connection
    }

    /**
     * Force stop all tasks running.
     * @param waitInSecAfterStop The sleep time after all task stopped. (default to 5)
     */
    async stopAll(waitInSecAfterStop: number = 5): Promise<void> {
        await c.TaskManager.stopAll(waitInSecAfterStop)
    }

    /**
     * Stop a task by name
     * @param taskName The task name
     */
    stop(taskName: string): void {
        c.TaskManager.stop(taskName)
    }

    /**
     * Checks whether a task is running
     * @param name The task name
     * @returns true if the task is running
     */
    isRunning(name:string):boolean {
        return c.TaskManager.isRunning(name)
    }

    /**
     * Checks whether a task is completed
     * @param name The task name
     * @returns true if task is completed
     */
    isTaskCompleted(name:string): boolean {
        return c.TaskManager.isTaskCompleted(name)
    }

    /**
     * Gets all task states
     * @returns all task states 
     */
    getAllState():Array<{ name: string; state: TaskState }> {
        return c.TaskManager.getAllState()
    }

    /**
     * Adds a task with a name
     * @param name The task name
     * @param task The task to register 
     */
    addTask(name: string, task: Task): void {
        c.TaskManager.addTask(name, task)
    }

    /**
     * Starts the polling task to observe a message type
     * @param name The task name
     * @param address  THe address
     * @param callbackMap the callback function-map
     * @param accessTokenRecovery The function to renew the access token
     * @param waitForInSec The interval to observe
     */
    async startReceiveNotificationProcess(
        name: string,
        address: string,
        callbackMap: Map<Messaging.NotificationTypes, (notification: Messaging.INotification) => Promise<void>>,
        accessTokenRecovery: (error: Error) => Promise<void>,
        waitForInSec?: number
    ): Promise<string> {
        return c.TaskManager.startReceiveNotificationProcess(this.connection, name, address, callbackMap, accessTokenRecovery, waitForInSec)    
    }

    /**
     * Starts a polling task to observe a message type
     * @param name The task name
     * @param contracts The contracts to observe
     * @param storage The data storage to store the last blocknumber
     * @param eventCallbackMap The event callback map for each contracts
     * @param initialBlockNumber The initial blocknumber to start the event observation
     * @param waitForInSec The observation interval
     * @param chunkSize The chunk size for event processing
     */
    async startEthEventObserver(
        name: string,
        contracts: any[],
        storage: IDataStorage,
        eventCallbackMap: Map<string, Map<EthEventTypes, (event: any) => Promise<void>>>,
        initialBlockNumber?: number,
        waitForInSec?: number,
        chunkSize?: number
    ): Promise<string> {
        return c.TaskManager.startEthEventObserver(
            this.connection,
            name,
            contracts,
            storage, 
            eventCallbackMap,
            initialBlockNumber,
            waitForInSec,
            chunkSize
        )
    }
}

export default TaskManager